import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tag, Button, Space, Spin, Alert, Typography } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getPanelFarmaciasSinVincular,
  type FarmaciaSinVincular,
} from '../../api/vinculacionApi';
import { CrmCliente, CrmFarmacia, exportCsv, FilterBtn, cmpNum, cmpTxt } from './helpers';

const { Paragraph, Text } = Typography;

const VinculacionFarmaciasSinVincularTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState<FarmaciaSinVincular[]>([]);
  const [filtro, setFiltro] = useState<'' | 'con' | 'sin'>('');

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      setRows(await getPanelFarmaciasSinVincular());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(
    () => ({ con: rows.filter((r) => r.titulares.length > 0).length, sin: rows.filter((r) => r.titulares.length === 0).length }),
    [rows],
  );
  const data = filtro === 'con' ? rows.filter((r) => r.titulares.length > 0) : filtro === 'sin' ? rows.filter((r) => r.titulares.length === 0) : rows;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error) {
    return <Alert type="error" showIcon message="No se pudieron cargar las farmacias sin vincular" action={<Button onClick={load}>Reintentar</Button>} />;
  }

  const columns: ColumnsType<FarmaciaSinVincular> = [
    { title: 'ID', dataIndex: 'id', align: 'right', sorter: (a, b) => cmpNum(a.id, b.id) },
    { title: 'Farmacia', key: 'farmacia', sorter: (a, b) => cmpTxt(a.farmacia, b.farmacia), render: (_, r) => <CrmFarmacia id={r.id}>{r.farmacia || '(ver ficha)'}</CrmFarmacia> },
    { title: 'Población', dataIndex: 'poblacion', sorter: (a, b) => cmpTxt(a.poblacion, b.poblacion) },
    { title: 'Provincia', dataIndex: 'provincia', sorter: (a, b) => cmpTxt(a.provincia, b.provincia) },
    { title: 'Titular_ID', key: 'titIds', render: (_, r) => r.titulares.map((t) => t.id).join(', ') },
    {
      title: 'Titular',
      key: 'titNombres',
      render: (_, r) =>
        r.titulares.map((t, i) => (
          <React.Fragment key={t.id}>
            {i > 0 && ', '}
            <CrmCliente id={t.id}>{t.nombre || '(sin nombre)'}</CrmCliente>
          </React.Fragment>
        )),
    },
  ];

  const doExport = () =>
    exportCsv(
      'farmacias_gescom_sin_vincular.csv',
      ['ID', 'Farmacia', 'Poblacion', 'Provincia', 'Titular_ID', 'Titular'],
      data.map((r) => [
        r.id,
        r.farmacia ?? '',
        r.poblacion ?? '',
        r.provincia ?? '',
        r.titulares.map((t) => t.id).join(', '),
        r.titulares.map((t) => t.nombre ?? '').join(', '),
      ]),
    );

  return (
    <div>
      <Space wrap style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Space wrap>
          <Text type="secondary">Filtrar:</Text>
          <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>Todas ({rows.length})</FilterBtn>
          <FilterBtn active={filtro === 'con'} onClick={() => setFiltro('con')}>
            <Tag color="blue" style={{ marginInlineEnd: 4 }}>Con titular</Tag>
            {counts.con}
          </FilterBtn>
          <FilterBtn active={filtro === 'sin'} onClick={() => setFiltro('sin')}>
            <Tag style={{ marginInlineEnd: 4 }}>Sin titular</Tag>
            {counts.sin}
          </FilterBtn>
        </Space>
        <Space>
          <Button size="small" icon={<DownloadOutlined />} onClick={doExport}>Exportar CSV</Button>
          <Button size="small" icon={<ReloadOutlined />} onClick={load}>Actualizar</Button>
        </Space>
      </Space>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Farmacias activas en Gescom-AQ que no están vinculadas a ninguna farmacia de CU2, con sus titulares activos (si los hay).
      </Paragraph>
      <Table rowKey="id" size="small" columns={columns} dataSource={data} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />
    </div>
  );
};

export default VinculacionFarmaciasSinVincularTab;
